<script setup>
// Configurações → Pesquisa de Satisfação.
//
// Padrão: replica `Configurações → Caixas de Entrada → Editar` -
// `SettingsLayout` + `BaseSettingsHeader` no topo, `SettingsToggleSection`
// por indicador (header + toggle + slot editor) e `SettingsFieldSection`
// dentro do editor para a meta.
//
// Cada indicador tem escala NATIVA (não converte para %):
//   CSAT  → 1.0 a 5.0 (média das notas)
//   NPS   → -100 a +100 (% promotores − % detratores)
//   CES   → 1.0 a 7.0 (esforço médio; menor é melhor)
import { computed, onMounted, reactive, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStore, useMapGetter } from 'dashboard/composables/store';
import { useAlert } from 'dashboard/composables';

import SettingsLayout from '../SettingsLayout.vue';
import BaseSettingsHeader from '../components/BaseSettingsHeader.vue';
import SettingsToggleSection from 'dashboard/components-next/Settings/SettingsToggleSection.vue';
import SettingsFieldSection from 'dashboard/components-next/Settings/SettingsFieldSection.vue';
import NextButton from 'dashboard/components-next/button/Button.vue';

const { t } = useI18n();
const store = useStore();
const currentAccount = useMapGetter('getCurrentAccount');

const saving = ref(false);

const INDICATORS = [
  {
    key: 'csat',
    title: 'CSAT - Satisfação do cliente',
    description:
      'Mede satisfação após o atendimento. Respondido em escala de 5 carinhas.',
    rangeLabel: 'Escala de 1 a 5',
    min: 1,
    max: 5,
    step: 0.1,
    default: 4.0,
    higherIsBetter: true,
    buckets: [
      { max: 1.5, emoji: '😞', label: 'Ruim' },
      { max: 2.5, emoji: '😑', label: 'Fraco' },
      { max: 3.5, emoji: '😐', label: 'Regular' },
      { max: 4.5, emoji: '😀', label: 'Bom' },
      { max: 5.0, emoji: '😍', label: 'Excelente' },
    ],
    helpText: 'Valor mínimo para o indicador ficar verde nos relatórios.',
  },
  {
    key: 'nps',
    title: 'NPS - Net Promoter Score',
    description: 'Mede a probabilidade do cliente recomendar a empresa.',
    rangeLabel: 'Escala de -100 a +100',
    min: -100,
    max: 100,
    step: 1,
    default: 50,
    higherIsBetter: true,
    buckets: [
      { max: -1, emoji: '😞', label: 'Crítico' },
      { max: 30, emoji: '😐', label: 'Razoável' },
      { max: 50, emoji: '🙂', label: 'Bom' },
      { max: 70, emoji: '😀', label: 'Ótimo' },
      { max: 100, emoji: '😍', label: 'Excelente' },
    ],
    helpText: 'Valor mínimo para o indicador ficar verde nos relatórios.',
  },
  {
    key: 'ces',
    title: 'CES - Esforço do cliente',
    description:
      'Mede o esforço que o cliente teve para resolver sua demanda. Quanto menor, melhor.',
    rangeLabel: 'Escala de 1 a 7 (menor é melhor)',
    min: 1,
    max: 7,
    step: 0.1,
    default: 3.0,
    higherIsBetter: false,
    buckets: [
      { max: 2.5, emoji: '😍', label: 'Excelente' },
      { max: 3.5, emoji: '😀', label: 'Bom' },
      { max: 4.5, emoji: '😐', label: 'Médio' },
      { max: 5.5, emoji: '😑', label: 'Alto' },
      { max: 7.0, emoji: '😞', label: 'Crítico' },
    ],
    helpText: 'Valor máximo aceitável. Acima disso o indicador fica vermelho.',
  },
];

const state = reactive({
  csat: { enabled: true, target_score: 4.0 },
  nps: { enabled: false, target_score: 50 },
  ces: { enabled: false, target_score: 3.0 },
});

const hasAnyEnabled = computed(() =>
  INDICATORS.some(ind => state[ind.key].enabled)
);

function bucketFor(ind, value) {
  const v = Number(value);
  return (
    ind.buckets.find(b => v <= b.max) || ind.buckets[ind.buckets.length - 1]
  );
}

function formatTarget(ind, value) {
  const v = Number(value) || 0;
  return ind.step >= 1 ? v.toFixed(0) : v.toFixed(1);
}

function clamp(ind, value) {
  const v = Number(value);
  if (Number.isNaN(v)) return ind.default;
  if (v < ind.min) return ind.min;
  if (v > ind.max) return ind.max;
  return v;
}

function onTargetChange(ind, event) {
  state[ind.key].target_score = clamp(ind, event.target.value);
}

function rehydrate() {
  const stored = currentAccount.value?.survey_settings || {};
  INDICATORS.forEach(ind => {
    const cfg = stored[ind.key] || stored[ind.key.toUpperCase()] || {};
    const ts = Number(cfg.target_score);
    const target =
      !Number.isNaN(ts) && ts >= ind.min && ts <= ind.max ? ts : ind.default;
    state[ind.key] = {
      enabled: cfg.enabled ?? state[ind.key].enabled,
      target_score: target,
    };
  });
}

onMounted(rehydrate);

async function save() {
  saving.value = true;
  try {
    await store.dispatch('accounts/update', {
      survey_settings: {
        csat: {
          enabled: state.csat.enabled,
          target_score: state.csat.target_score,
        },
        nps: {
          enabled: state.nps.enabled,
          target_score: state.nps.target_score,
        },
        ces: {
          enabled: state.ces.enabled,
          target_score: state.ces.target_score,
        },
      },
    });
    useAlert('Pesquisa de Satisfação salva.');
  } catch (e) {
    useAlert(e?.message || 'Falha ao salvar.');
  } finally {
    saving.value = false;
  }
}
</script>

<template>
  <SettingsLayout class="w-full max-w-3xl ltr:mr-auto rtl:ml-auto">
    <template #header>
      <BaseSettingsHeader
        :title="t('SATISFACTION_SURVEY.HEADER')"
        :description="t('SATISFACTION_SURVEY.SETTINGS.DESCRIPTION')"
        feature-name="satisfaction_survey"
      />
    </template>

    <template #body>
      <div class="flex flex-col gap-4 pb-10">
        <!-- Cada indicador é um SettingsToggleSection - mesmo componente das
             configurações de Inbox. Header com título + toggle nativo;
             slot editor abre quando ligado e contém o input de meta. -->
        <SettingsToggleSection
          v-for="ind in INDICATORS"
          :key="ind.key"
          v-model="state[ind.key].enabled"
          :header="ind.title"
          :description="ind.description"
        >
          <template v-if="state[ind.key].enabled" #editor>
            <SettingsFieldSection label="Meta" :help-text="ind.helpText">
              <div class="flex items-center gap-3 flex-wrap">
                <input
                  :value="formatTarget(ind, state[ind.key].target_score)"
                  type="number"
                  :min="ind.min"
                  :max="ind.max"
                  :step="ind.step"
                  class="w-28 rounded-md border border-n-weak bg-n-solid-1 px-3 py-1.5 text-body-main text-n-slate-12 focus:outline-none focus:border-n-brand"
                  @change="event => onTargetChange(ind, event)"
                  @blur="event => onTargetChange(ind, event)"
                />
                <span class="text-body-small text-n-slate-11">{{
                  ind.rangeLabel
                }}</span>

                <div
                  class="ml-auto inline-flex items-center gap-2 px-3 py-1.5 rounded-md bg-n-alpha-1"
                >
                  <span class="text-xl leading-none">
                    {{ bucketFor(ind, state[ind.key].target_score).emoji }}
                  </span>
                  <div class="text-body-small text-n-slate-12">
                    <span class="text-n-slate-11">Sua meta:</span>
                    <b class="ltr:ml-1 rtl:mr-1">{{
                      bucketFor(ind, state[ind.key].target_score).label
                    }}</b>
                    <span class="text-n-slate-11">·</span>
                    {{ formatTarget(ind, state[ind.key].target_score) }}
                  </div>
                </div>
              </div>
            </SettingsFieldSection>
          </template>
        </SettingsToggleSection>

        <!-- Aviso quando nada ativo -->
        <div
          v-if="!hasAnyEnabled"
          class="flex items-start gap-3 p-3 rounded-xl bg-n-amber-3 border border-n-amber-7"
        >
          <i class="i-lucide-alert-triangle size-4 text-n-amber-11 mt-0.5" />
          <p class="text-body-small text-n-amber-12 mb-0">
            Nenhum indicador ativo. Habilite ao menos um para que os relatórios
            de Pesquisa de Satisfação exibam dados.
          </p>
        </div>

        <div class="flex justify-start pt-2">
          <NextButton
            :is-loading="saving"
            label="Salvar configurações"
            icon="i-lucide-save"
            @click="save"
          />
        </div>

        <p class="text-body-small text-n-slate-11 mt-2 mb-0">
          Os dados coletados aparecem em Relatórios → Pesquisa de Satisfação,
          respeitando as marcações desta tela.
        </p>
      </div>
    </template>
  </SettingsLayout>
</template>
